import { FEATURE_KEYS, MODULE_KEYS, OPERATION_IDS } from './routes/ids.js';
import { ACCOUNTING_GENERAL_LEDGER, ACCOUNTING_MANAGEMENT_FEE_LIST, CASH_POSITIONS_LIST, CORPORATE_ACTION_LIST, FACT_VALUATION, HOLDINGS_VIEW, REPORT_EXPORT, REPORTS_VIEW, } from '../operations/index.js';
const tk = (value) => value;
const qdi = (value) => value;
const fid = (value) => value;
const formId = (value) => value;
const operation = (value) => value;
const telemetry = (value) => value;
export const PMS_OPERATIONAL_REPORT_IDS = Object.freeze({
    CASH_POSITION: 'report.portfolio.cash-position',
    CASH_MOVEMENT: 'report.portfolio.cash-movement',
    INVESTMENT_INCOME: 'report.portfolio.investment-income',
    FEES_EXPENSES: 'report.portfolio.fees-expenses',
    TAX_LOTS: 'report.portfolio.tax-lots-cost-basis',
    CORPORATE_ACTIONS: 'report.portfolio.corporate-actions',
    GENERAL_LEDGER: 'report.accounting.general-ledger',
    VALUATION_QUALITY: 'report.portfolio.valuation-quality',
    ALLOCATION_EXPOSURE: 'report.portfolio.allocation-exposure',
    MANAGEMENT_SUMMARY: 'report.management.portfolio-summary',
});
function metadata(id, accessibilityLabelKey, operationId = REPORTS_VIEW) {
    return {
        id,
        schemaVersion: 1,
        module: MODULE_KEYS.REPORTS,
        feature: FEATURE_KEYS.REPORTS_VIEW,
        operationId: operation(operationId),
        privacyClassification: 'FINANCIAL',
        telemetryId: telemetry(`experience.${id}`),
        accessibilityLabelKey: tk(accessibilityLabelKey),
        availability: 'AVAILABLE',
        owner: 'investment-reporting-ux',
    };
}
function field(id, name, labelKey, fieldType, overrides = {}) {
    return {
        ...metadata(id, labelKey),
        id: fid(id),
        name,
        labelKey: tk(labelKey),
        fieldType,
        value: { submitBehavior: 'include', hiddenValuePolicy: 'unregister' },
        layout: { xs: 12, sm: 6, md: 4 },
        ...overrides,
    };
}
function clientField(prefix) {
    return field(`field.reports.${prefix}.clientAccountId`, 'clientAccountId', 'reports.liveParameters.clientAccount', 'select', {
        optionSource: {
            adapterId: 'api',
            operationId: OPERATION_IDS.ORGANIZATION_CLIENTS_LIST,
            valuePath: 'id',
            labelPath: 'fullName',
            allowSearch: true,
            minimumSearchLength: 0,
        },
    });
}
function portfolioField(prefix) {
    return field(`field.reports.${prefix}.portfolioAccountId`, 'portfolioAccountId', 'reports.liveParameters.portfolioAccount', 'select', {
        optionSource: {
            adapterId: 'api',
            operationId: OPERATION_IDS.ORGANIZATION_PORTFOLIOS_LIST,
            valuePath: 'id',
            labelPath: 'accountNumber',
            allowSearch: true,
            minimumSearchLength: 0,
        },
    });
}
function instrumentField(prefix) {
    return field(`field.reports.${prefix}.instrumentSymbol`, 'instrumentSymbol', 'reports.liveParameters.instrument', 'text');
}
function dateFields(prefix) {
    return [
        field(`field.reports.${prefix}.from`, 'from', 'reports.liveParameters.from', 'date'),
        field(`field.reports.${prefix}.to`, 'to', 'reports.liveParameters.to', 'date'),
    ];
}
function asOfField(prefix) {
    return field(`field.reports.${prefix}.asOfDate`, 'asOfDate', 'reports.liveParameters.asOfDate', 'date');
}
function liveRunAction(id) {
    return {
        id: `action.${id}.run`,
        schemaVersion: 1,
        module: MODULE_KEYS.REPORTS,
        feature: FEATURE_KEYS.REPORTS_VIEW,
        privacyClassification: 'FINANCIAL',
        telemetryId: telemetry(`experience.action.${id}.run`),
        accessibilityLabelKey: tk('reports.liveParameters.run'),
        availability: 'AVAILABLE',
        owner: 'experience-registry',
        labelKey: tk('reports.liveParameters.run'),
        iconKey: 'play',
        kind: 'command',
        commandId: `reports.live.run.${id}`,
        method: 'GET',
        intent: 'primary',
        idempotencyRequired: false,
        submitBehavior: 'form_submit',
        submitConfig: {
            validatesForm: true,
            requiresDirty: false,
            requiresValid: true,
            resetBehavior: 'none',
        },
        successBehavior: 'NO_OP',
        errorBehavior: 'SHOW_INLINE_ERROR',
        errorMessageKey: tk('reports.liveParameters.requestFailed'),
        order: 10,
        group: 'primary',
    };
}
function parameterForm(id, fields) {
    return {
        ...metadata(id, 'a11y.form.reports.parameters'),
        id: formId(id),
        titleKey: tk('reports.liveParameters.title'),
        descriptionKey: tk('reports.liveParameters.description'),
        mode: 'create',
        fields,
        actions: [liveRunAction(id)],
        submission: {
            defaultCommandId: `reports.live.run.${id}`,
            includeFields: fields.map((item) => item.id),
        },
    };
}
export const CASH_POSITION_REPORT_FORM = parameterForm('form.reports.cashPositionParameters', [
    clientField('cashPosition'),
    portfolioField('cashPosition'),
    field('field.reports.cashPosition.currency', 'currency', 'reports.parameters.currency', 'text'),
]);
export const CASH_MOVEMENT_REPORT_FORM = parameterForm('form.reports.cashMovementParameters', [
    ...dateFields('cashMovement'),
    clientField('cashMovement'),
    portfolioField('cashMovement'),
    field('field.reports.cashMovement.currency', 'currency', 'reports.parameters.currency', 'text'),
]);
export const INVESTMENT_INCOME_REPORT_FORM = parameterForm('form.reports.investmentIncomeParameters', [
    ...dateFields('investmentIncome'),
    clientField('investmentIncome'),
    portfolioField('investmentIncome'),
    instrumentField('investmentIncome'),
]);
export const FEES_EXPENSES_REPORT_FORM = parameterForm('form.reports.feesExpensesParameters', [
    ...dateFields('feesExpenses'),
    clientField('feesExpenses'),
    portfolioField('feesExpenses'),
]);
export const TAX_LOTS_REPORT_FORM = parameterForm('form.reports.taxLotsParameters', [
    clientField('taxLots'),
    portfolioField('taxLots'),
    instrumentField('taxLots'),
]);
export const CORPORATE_ACTIONS_REPORT_FORM = parameterForm('form.reports.corporateActionsParameters', [
    ...dateFields('corporateActions'),
    clientField('corporateActions'),
    portfolioField('corporateActions'),
    instrumentField('corporateActions'),
]);
export const GENERAL_LEDGER_REPORT_FORM = parameterForm('form.reports.generalLedgerParameters', [
    ...dateFields('generalLedger'),
    field('field.reports.generalLedger.accountCode', 'accountCode', 'reports.parameters.accountCode', 'text', { helpTextKey: tk('helpText.field.reports.accountCode') }),
]);
export const VALUATION_QUALITY_REPORT_FORM = parameterForm('form.reports.valuationQualityParameters', [
    asOfField('valuationQuality'),
    clientField('valuationQuality'),
    portfolioField('valuationQuality'),
    instrumentField('valuationQuality'),
]);
export const ALLOCATION_EXPOSURE_REPORT_FORM = parameterForm('form.reports.allocationExposureParameters', [
    asOfField('allocationExposure'),
    clientField('allocationExposure'),
    portfolioField('allocationExposure'),
    field('field.reports.allocationExposure.dimension', 'dimension', 'reports.parameters.dimension', 'select', {
        helpTextKey: tk('helpText.field.reports.dimension'),
        options: [
            { value: 'CURRENCY', labelKey: tk('reports.options.dimension.currency') },
            { value: 'INSTRUMENT', labelKey: tk('reports.options.dimension.instrument') },
            { value: 'INSTRUMENT_TYPE', labelKey: tk('reports.options.dimension.instrumentType') },
            { value: 'SECTOR', labelKey: tk('reports.options.dimension.sector') },
        ],
    }),
]);
export const MANAGEMENT_SUMMARY_REPORT_FORM = parameterForm('form.reports.managementSummaryParameters', [asOfField('managementSummary')]);
function column(id, fieldName, labelKey, fieldType) {
    return {
        ...metadata(id, labelKey),
        labelKey: tk(labelKey),
        field: fieldName,
        fieldType,
        defaultVisible: true,
        hideable: true,
        sortable: false,
        filterable: false,
        searchable: false,
        groupable: false,
        requiredVisible: false,
        resizable: true,
        reorderable: true,
        pinnable: false,
        defaultWidth: fieldType === 'money' ? 155 : 140,
        minWidth: 90,
        maxWidth: 340,
        align: fieldType === 'money'
            || fieldType === 'number'
            || fieldType === 'percentage'
            || fieldType === 'quantity'
            ? 'right'
            : 'left',
        nullDisplayBehavior: 'dash',
    };
}
const c = column;
const CASH_POSITION_COLUMNS = [
    c('column.report.cashPosition.portfolio', 'portfolioAccountId', 'reports.columns.portfolioAccount', 'reference'),
    c('column.report.cashPosition.currency', 'currency', 'reports.columns.currency', 'text'),
    c('column.report.cashPosition.balance', 'currentBalance', 'reports.columns.currentBalance', 'money'),
    c('column.report.cashPosition.updatedAt', 'updatedAt', 'reports.columns.updatedAt', 'datetime'),
];
const CASH_MOVEMENT_COLUMNS = [
    c('column.report.cashMovement.date', 'businessDate', 'reports.columns.businessDate', 'date'),
    c('column.report.cashMovement.type', 'movementType', 'reports.columns.movementType', 'text'),
    c('column.report.cashMovement.portfolio', 'sourcePortfolioAccountId', 'reports.columns.portfolioAccount', 'reference'),
    c('column.report.cashMovement.currency', 'currency', 'reports.columns.currency', 'text'),
    c('column.report.cashMovement.amount', 'amount', 'reports.columns.amount', 'money'),
    c('column.report.cashMovement.status', 'status', 'reports.columns.status', 'status'),
    c('column.report.cashMovement.reference', 'bankReference', 'reports.columns.reference', 'text'),
];
const INCOME_COLUMNS = [
    c('column.report.income.date', 'incomeDate', 'reports.columns.incomeDate', 'date'),
    c('column.report.income.type', 'incomeType', 'reports.columns.incomeType', 'text'),
    c('column.report.income.instrument', 'instrumentSymbol', 'reports.columns.instrument', 'text'),
    c('column.report.income.portfolio', 'portfolioAccountId', 'reports.columns.portfolioAccount', 'reference'),
    c('column.report.income.currency', 'currency', 'reports.columns.currency', 'text'),
    c('column.report.income.gross', 'grossAmount', 'reports.columns.grossAmount', 'money'),
    c('column.report.income.tax', 'withholdingTaxAmount', 'reports.columns.withholdingTaxAmount', 'money'),
    c('column.report.income.net', 'netAmount', 'reports.columns.netAmount', 'money'),
    c('column.report.income.status', 'status', 'reports.columns.status', 'status'),
];
const FEES_COLUMNS = [
    c('column.report.fees.periodStart', 'periodStart', 'reports.columns.periodStart', 'date'),
    c('column.report.fees.periodEnd', 'periodEnd', 'reports.columns.periodEnd', 'date'),
    c('column.report.fees.feeType', 'feeType', 'reports.columns.feeType', 'text'),
    c('column.report.fees.currency', 'currency', 'reports.columns.currency', 'text'),
    c('column.report.fees.net', 'netAmount', 'reports.columns.netAmount', 'money'),
    c('column.report.fees.tax', 'taxAmount', 'reports.columns.taxAmount', 'money'),
    c('column.report.fees.gross', 'grossAmount', 'reports.columns.grossAmount', 'money'),
    c('column.report.fees.status', 'status', 'reports.columns.status', 'status'),
];
const LOT_COLUMNS = [
    c('column.report.lots.date', 'acquisitionDate', 'reports.columns.acquisitionDate', 'date'),
    c('column.report.lots.instrument', 'instrumentSymbol', 'reports.columns.instrument', 'text'),
    c('column.report.lots.portfolio', 'portfolioAccountId', 'reports.columns.portfolioAccount', 'reference'),
    c('column.report.lots.originalQty', 'originalQuantity', 'reports.columns.originalQuantity', 'quantity'),
    c('column.report.lots.remainingQty', 'remainingQuantity', 'reports.columns.remainingQuantity', 'quantity'),
    c('column.report.lots.costPerUnit', 'costPerUnit', 'reports.columns.costPerUnit', 'money'),
    c('column.report.lots.remainingCost', 'remainingCostBasis', 'reports.columns.remainingCostBasis', 'money'),
    c('column.report.lots.source', 'source', 'reports.columns.source', 'text'),
];
const CORPORATE_ACTION_COLUMNS = [
    c('column.report.ca.recordDate', 'recordDate', 'reports.columns.recordDate', 'date'),
    c('column.report.ca.instrument', 'instrumentSymbol', 'reports.columns.instrument', 'text'),
    c('column.report.ca.type', 'actionType', 'reports.columns.actionType', 'text'),
    c('column.report.ca.status', 'status', 'reports.columns.status', 'status'),
    c('column.report.ca.entitlements', 'entitlementCount', 'reports.columns.entitlementCount', 'number'),
    c('column.report.ca.cash', 'cashAmountByCurrency', 'reports.columns.cashImpact', 'text'),
    c('column.report.ca.quantity', 'quantityImpact', 'reports.columns.quantityImpact', 'quantity'),
];
const GENERAL_LEDGER_COLUMNS = [
    c('column.report.gl.date', 'entryDate', 'reports.columns.postingDate', 'date'),
    c('column.report.gl.entry', 'entryNumber', 'reports.columns.journal', 'text'),
    c('column.report.gl.code', 'accountCode', 'reports.columns.accountCode', 'text'),
    c('column.report.gl.name', 'accountName', 'reports.columns.accountName', 'text'),
    c('column.report.gl.description', 'description', 'reports.columns.description', 'text'),
    c('column.report.gl.debit', 'debit', 'reports.columns.debit', 'money'),
    c('column.report.gl.credit', 'credit', 'reports.columns.credit', 'money'),
    c('column.report.gl.reference', 'reference', 'reports.columns.reference', 'text'),
];
const VALUATION_QUALITY_COLUMNS = [
    c('column.report.vq.asOf', 'asOfDate', 'reports.columns.asOfDate', 'date'),
    c('column.report.vq.instrument', 'instrumentSymbol', 'reports.columns.instrument', 'text'),
    c('column.report.vq.portfolio', 'portfolioAccountId', 'reports.columns.portfolioAccount', 'reference'),
    c('column.report.vq.currency', 'currency', 'reports.columns.currency', 'text'),
    c('column.report.vq.price', 'marketPrice', 'reports.columns.marketPrice', 'money'),
    c('column.report.vq.value', 'marketValue', 'reports.columns.marketValue', 'money'),
    c('column.report.vq.state', 'qualityState', 'reports.columns.qualityState', 'status'),
    c('column.report.vq.source', 'source', 'reports.columns.source', 'text'),
];
const ALLOCATION_COLUMNS = [
    c('column.report.allocation.dimension', 'dimensionValue', 'reports.columns.dimension', 'text'),
    c('column.report.allocation.currency', 'currency', 'reports.columns.currency', 'text'),
    c('column.report.allocation.value', 'marketValue', 'reports.columns.marketValue', 'money'),
    c('column.report.allocation.weight', 'weightPct', 'reports.columns.weightPct', 'percentage'),
    c('column.report.allocation.positions', 'positionCount', 'reports.columns.positions', 'number'),
];
const MANAGEMENT_COLUMNS = [
    c('column.report.management.client', 'clientReference', 'reports.columns.clientAccount', 'text'),
    c('column.report.management.portfolio', 'portfolioReference', 'reports.columns.portfolioAccount', 'text'),
    c('column.report.management.currency', 'currency', 'reports.columns.currency', 'text'),
    c('column.report.management.value', 'marketValue', 'reports.columns.marketValue', 'money'),
    c('column.report.management.cost', 'totalCost', 'reports.columns.totalCost', 'money'),
    c('column.report.management.pnl', 'totalGainLoss', 'reports.columns.totalGainLoss', 'money'),
    c('column.report.management.missing', 'missingValuationCount', 'reports.summary.missingValuationCount', 'number'),
];
export const ALLOCATION_EXPOSURE_VISUALIZATION = {
    id: 'visualization.report.portfolio.allocation-exposure',
    titleKey: tk('reports.visualizations.allocation.title'),
    descriptionKey: tk('reports.visualizations.allocation.description'),
    type: 'HORIZONTAL_BAR',
    dataSourceId: qdi('query.reports.allocation_exposure'),
    categoryField: 'dimensionValue',
    series: [{
            id: 'weightPct',
            field: 'weightPct',
            labelKey: tk('reports.columns.weightPct'),
            valueKind: 'PERCENT',
            currencySource: 'NONE',
            precision: 2,
        }],
    sort: { field: 'weightPct', direction: 'DESC' },
    limit: 12,
    accessibleTable: {
        enabled: true,
        captionKey: tk('reports.visualizations.allocation.tableCaption'),
        columns: [
            { field: 'dimensionValue', labelKey: tk('reports.columns.dimension'), valueKind: 'TEXT' },
            { field: 'weightPct', labelKey: tk('reports.columns.weightPct'), valueKind: 'PERCENT', currencySource: 'NONE', precision: 2 },
            { field: 'marketValue', labelKey: tk('reports.columns.marketValue'), valueKind: 'MONEY', currencySource: 'ROW_CURRENCY', currencyField: 'currency', precision: 2 },
            { field: 'currency', labelKey: tk('reports.columns.currency'), valueKind: 'TEXT' },
        ],
    },
    emptyStateKey: tk('reports.visualizations.allocation.empty'),
    partialStateKey: tk('reports.visualizations.allocation.partial'),
    minHeight: 280,
};
function report(options) {
    return {
        ...metadata(options.id, options.titleKey, options.operationId),
        reportType: options.reportType,
        titleKey: tk(options.titleKey),
        descriptionKey: tk(options.descriptionKey),
        category: options.category,
        scope: options.scope ?? 'BRANCH',
        routeId: 'reports',
        dataSourceId: qdi(`query.reports.${options.reportType.toLowerCase()}`),
        parameterFormId: String(options.form.id),
        runFormId: String(options.form.id),
        rowIdField: 'id',
        columns: options.columns,
        visualizations: options.visualizations,
        formats: ['json'],
        allowedFormats: ['json'],
        generatorId: `live.generator.${options.reportType.toLowerCase()}`,
        executionMode: 'LIVE_QUERY',
        operationBindings: {
            listOperationId: options.operationId,
            runOperationId: options.operationId,
            exportOperationId: REPORT_EXPORT,
        },
        lineage: {
            sourceSystem: 'canonical-financial-authorities',
            sourceEntities: options.sourceEntities,
            calculationVersion: options.calculationVersion,
        },
        implemented: true,
        auditPolicy: 'ALWAYS',
    };
}
const definitions = [
    [PMS_OPERATIONAL_REPORT_IDS.CASH_POSITION, report({
            id: PMS_OPERATIONAL_REPORT_IDS.CASH_POSITION,
            reportType: 'CASH_POSITION',
            titleKey: 'reports.types.cashPosition',
            descriptionKey: 'reports.descriptions.cashPosition',
            category: 'PORTFOLIO',
            operationId: CASH_POSITIONS_LIST,
            form: CASH_POSITION_REPORT_FORM,
            columns: CASH_POSITION_COLUMNS,
            sourceEntities: ['CashPosition'],
            calculationVersion: 'current-cash-position-v1',
        })],
    [PMS_OPERATIONAL_REPORT_IDS.CASH_MOVEMENT, report({
            id: PMS_OPERATIONAL_REPORT_IDS.CASH_MOVEMENT,
            reportType: 'CASH_MOVEMENT',
            titleKey: 'reports.types.cashMovement',
            descriptionKey: 'reports.descriptions.cashMovement',
            category: 'TRANSACTIONS',
            operationId: CASH_POSITIONS_LIST,
            form: CASH_MOVEMENT_REPORT_FORM,
            columns: CASH_MOVEMENT_COLUMNS,
            sourceEntities: ['CashMovement'],
            calculationVersion: 'posted-cash-movement-v1',
        })],
    [PMS_OPERATIONAL_REPORT_IDS.INVESTMENT_INCOME, report({
            id: PMS_OPERATIONAL_REPORT_IDS.INVESTMENT_INCOME,
            reportType: 'INVESTMENT_INCOME',
            titleKey: 'reports.types.investmentIncome',
            descriptionKey: 'reports.descriptions.investmentIncome',
            category: 'PORTFOLIO',
            operationId: HOLDINGS_VIEW,
            form: INVESTMENT_INCOME_REPORT_FORM,
            columns: INCOME_COLUMNS,
            sourceEntities: ['CorporateActionCashReceipt', 'TaxWithholdingFact', 'FixedIncomeLifecycleEvent'],
            calculationVersion: 'recognized-investment-income-v1',
        })],
    [PMS_OPERATIONAL_REPORT_IDS.FEES_EXPENSES, report({
            id: PMS_OPERATIONAL_REPORT_IDS.FEES_EXPENSES,
            reportType: 'FEES_EXPENSES',
            titleKey: 'reports.types.feesExpenses',
            descriptionKey: 'reports.descriptions.feesExpenses',
            category: 'ACCOUNTING',
            operationId: ACCOUNTING_MANAGEMENT_FEE_LIST,
            form: FEES_EXPENSES_REPORT_FORM,
            columns: FEES_COLUMNS,
            sourceEntities: ['ManagementFeeInvoice', 'ManagementFeeAccrual', 'PerformanceFeeInvoice', 'PerformanceFeeCalculation'],
            calculationVersion: 'approved-fee-records-v1',
        })],
    [PMS_OPERATIONAL_REPORT_IDS.TAX_LOTS, report({
            id: PMS_OPERATIONAL_REPORT_IDS.TAX_LOTS,
            reportType: 'TAX_LOTS',
            titleKey: 'reports.types.taxLots',
            descriptionKey: 'reports.descriptions.taxLots',
            category: 'PORTFOLIO',
            operationId: HOLDINGS_VIEW,
            form: TAX_LOTS_REPORT_FORM,
            columns: LOT_COLUMNS,
            sourceEntities: ['HoldingLot'],
            calculationVersion: 'current-lot-cost-basis-v1',
        })],
    [PMS_OPERATIONAL_REPORT_IDS.CORPORATE_ACTIONS, report({
            id: PMS_OPERATIONAL_REPORT_IDS.CORPORATE_ACTIONS,
            reportType: 'CORPORATE_ACTIONS',
            titleKey: 'reports.types.corporateActions',
            descriptionKey: 'reports.descriptions.corporateActions',
            category: 'PORTFOLIO',
            operationId: CORPORATE_ACTION_LIST,
            form: CORPORATE_ACTIONS_REPORT_FORM,
            columns: CORPORATE_ACTION_COLUMNS,
            sourceEntities: ['CorporateAction', 'CorporateActionEntitlement', 'CorporateActionCashReceipt'],
            calculationVersion: 'corporate-action-entitlement-projection-v1',
        })],
    [PMS_OPERATIONAL_REPORT_IDS.GENERAL_LEDGER, report({
            id: PMS_OPERATIONAL_REPORT_IDS.GENERAL_LEDGER,
            reportType: 'GENERAL_LEDGER',
            titleKey: 'reports.types.generalLedger',
            descriptionKey: 'reports.descriptions.generalLedger',
            category: 'ACCOUNTING',
            scope: 'ORGANIZATION',
            operationId: ACCOUNTING_GENERAL_LEDGER,
            form: GENERAL_LEDGER_REPORT_FORM,
            columns: GENERAL_LEDGER_COLUMNS,
            sourceEntities: ['JournalEntry', 'JournalEntryLine', 'LedgerAccount'],
            calculationVersion: 'posted-general-ledger-v1',
        })],
    [PMS_OPERATIONAL_REPORT_IDS.VALUATION_QUALITY, report({
            id: PMS_OPERATIONAL_REPORT_IDS.VALUATION_QUALITY,
            reportType: 'VALUATION_QUALITY',
            titleKey: 'reports.types.valuationQuality',
            descriptionKey: 'reports.descriptions.valuationQuality',
            category: 'PORTFOLIO',
            operationId: FACT_VALUATION,
            form: VALUATION_QUALITY_REPORT_FORM,
            columns: VALUATION_QUALITY_COLUMNS,
            sourceEntities: ['PositionFact', 'ValuationFact', 'Instrument'],
            calculationVersion: 'latest-valuation-quality-v1-active-positions',
        })],
    [PMS_OPERATIONAL_REPORT_IDS.ALLOCATION_EXPOSURE, report({
            id: PMS_OPERATIONAL_REPORT_IDS.ALLOCATION_EXPOSURE,
            reportType: 'ALLOCATION_EXPOSURE',
            titleKey: 'reports.types.allocationExposure',
            descriptionKey: 'reports.descriptions.allocationExposure',
            category: 'PORTFOLIO',
            operationId: HOLDINGS_VIEW,
            form: ALLOCATION_EXPOSURE_REPORT_FORM,
            columns: ALLOCATION_COLUMNS,
            sourceEntities: ['PositionFact', 'ValuationFact', 'Instrument', 'Sector'],
            calculationVersion: 'valuation-allocation-v1-active-positions',
            visualizations: [ALLOCATION_EXPOSURE_VISUALIZATION],
        })],
    [PMS_OPERATIONAL_REPORT_IDS.MANAGEMENT_SUMMARY, report({
            id: PMS_OPERATIONAL_REPORT_IDS.MANAGEMENT_SUMMARY,
            reportType: 'MANAGEMENT_SUMMARY',
            titleKey: 'reports.types.managementSummary',
            descriptionKey: 'reports.descriptions.managementSummary',
            category: 'MANAGEMENT',
            scope: 'ORGANIZATION',
            operationId: HOLDINGS_VIEW,
            form: MANAGEMENT_SUMMARY_REPORT_FORM,
            columns: MANAGEMENT_COLUMNS,
            sourceEntities: ['PositionFact', 'ValuationFact', 'PortfolioAccount', 'ClientAccount', 'Instrument'],
            calculationVersion: 'management-portfolio-summary-v1-active-positions',
        })],
];
export const PMS_OPERATIONAL_REPORT_REGISTRY = Object.freeze(Object.fromEntries(definitions));
export const PMS_OPERATIONAL_REPORT_FORMS = Object.freeze(Object.fromEntries([
    CASH_POSITION_REPORT_FORM,
    CASH_MOVEMENT_REPORT_FORM,
    INVESTMENT_INCOME_REPORT_FORM,
    FEES_EXPENSES_REPORT_FORM,
    TAX_LOTS_REPORT_FORM,
    CORPORATE_ACTIONS_REPORT_FORM,
    GENERAL_LEDGER_REPORT_FORM,
    VALUATION_QUALITY_REPORT_FORM,
    ALLOCATION_EXPOSURE_REPORT_FORM,
    MANAGEMENT_SUMMARY_REPORT_FORM,
].map((form) => [String(form.id), form])));
